import React from 'react';
import {
  ADMIN_DATE_PLACEHOLDER,
  adminImportProvenanceSourceLabel,
  adminRegistryActivityTypeLabel,
  formatAdminDate,
  formatAdminProspectCentsLabel,
  formatAdminProspectLandArea,
  formatAdminProspectUnits,
  formatAdminProspectUnavailable,
} from './adminProspectDisplay';

function Subsection({ title, children }) {
  return (
    <div className="rounded-lg border border-slate-100 bg-slate-50 p-3">
      <h3 className="text-xs font-semibold uppercase tracking-wide text-slate-600">{title}</h3>
      <dl className="mt-2 grid gap-2 sm:grid-cols-2">{children}</dl>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div className="min-w-0">
      <dt className="text-xs font-medium uppercase tracking-wide text-slate-500">{label}</dt>
      <dd className="mt-0.5 break-words text-sm text-slate-900">{children}</dd>
    </div>
  );
}

function displayValue(value, unavailable) {
  if (value == null || value === '') return unavailable;
  return value;
}

export default function ProspectImportedPropertyCard({
  detail,
  language,
  t,
  onEdit,
  hideTitle = false,
  embedded = false,
}) {
  const unavailable = formatAdminProspectUnavailable(t);
  const profile = detail?.latestImportedPropertyProfile || null;
  const assessment = detail?.latestMunicipalAssessment || null;
  const activity = detail?.latestRegistryActivity || null;
  const lotNumbers = detail?.lotNumbers || profile?.lotNumbers || [];
  const cadastralDivision = detail?.cadastralDivision || profile?.cadastralDivision || null;
  const municipalRollNumber = detail?.municipalRollNumber || profile?.municipalRollNumber || null;
  const provenance = profile?.provenance || assessment?.provenance || activity?.provenance || null;

  const centsLabel = (value) => {
    if (value === null) return unavailable;
    if (value === 0) return formatAdminProspectCentsLabel(0, language, t);
    return formatAdminProspectCentsLabel(value, language, t) || unavailable;
  };

  const Tag = 'section';
  const chrome = embedded
    ? 'min-w-0'
    : 'rounded-xl border border-slate-200 bg-white p-4';

  return (
    <Tag className={chrome} data-embedded={embedded ? 'true' : 'false'}>
      <div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
        <div>
          {!hideTitle ? (
            <h2 className="text-sm font-semibold text-slate-800">
              {t('admin.prospection.importedProperty.title')}
            </h2>
          ) : null}
          <p className={`${hideTitle ? 'mt-0' : 'mt-1'} text-xs text-slate-500`}>
            {t('admin.prospection.importedProperty.subtitle')}
          </p>
        </div>
        {onEdit ? (
          <button
            type="button"
            onClick={onEdit}
            className="rounded-lg border border-indigo-300 bg-white px-3 py-1.5 text-sm font-semibold text-indigo-700 hover:bg-indigo-50 focus:outline-none focus:ring-2 focus:ring-indigo-500"
          >
            {t('admin.prospection.importedProperty.edit')}
          </button>
        ) : null}
      </div>

      <div className="mt-4 space-y-3">
        <Subsection title={t('admin.prospection.importedProperty.section.cadastral')}>
          <Field label={t('admin.prospection.importedProperty.lotNumbers')}>
            {lotNumbers.length ? lotNumbers.join(', ') : unavailable}
          </Field>
          <Field label={t('admin.prospection.importedProperty.cadastralDivision')}>
            {displayValue(cadastralDivision, unavailable)}
          </Field>
          <Field label={t('admin.prospection.importedProperty.municipalRollNumber')}>
            {displayValue(municipalRollNumber, unavailable)}
          </Field>
        </Subsection>

        <Subsection title={t('admin.prospection.importedProperty.section.characteristics')}>
          <Field label={t('admin.prospection.importedProperty.buildingCategory')}>
            {displayValue(profile?.buildingCategory, unavailable)}
          </Field>
          <Field label={t('admin.prospection.importedProperty.constructionType')}>
            {displayValue(profile?.constructionType, unavailable)}
          </Field>
          <Field label={t('admin.prospection.importedProperty.numberOfUnits')}>
            {profile?.numberOfUnits != null
              ? formatAdminProspectUnits(profile.numberOfUnits)
              : unavailable}
          </Field>
          <Field label={t('admin.prospection.importedProperty.numberOfFloors')}>
            {profile?.numberOfFloors != null
              ? formatAdminProspectUnits(profile.numberOfFloors)
              : unavailable}
          </Field>
          <Field label={t('admin.prospection.importedProperty.constructionYear')}>
            {profile?.constructionYear != null ? String(profile.constructionYear) : unavailable}
          </Field>
          <Field label={t('admin.prospection.importedProperty.landArea')}>
            {profile?.landArea
              ? formatAdminProspectLandArea(profile.landArea, language, t)
              : unavailable}
          </Field>
        </Subsection>

        <Subsection title={t('admin.prospection.importedProperty.section.assessment')}>
          <Field label={t('admin.prospection.importedProperty.assessment.total')}>
            {centsLabel(assessment?.totalCents ?? null)}
          </Field>
          <Field label={t('admin.prospection.importedProperty.assessment.land')}>
            {centsLabel(assessment?.landCents ?? null)}
          </Field>
          <Field label={t('admin.prospection.importedProperty.assessment.building')}>
            {centsLabel(assessment?.buildingCents ?? null)}
          </Field>
          <Field label={t('admin.prospection.importedProperty.assessment.referenceYear')}>
            {assessment?.referenceYear != null ? String(assessment.referenceYear) : unavailable}
          </Field>
          <Field label={t('admin.prospection.importedProperty.assessment.referenceDate')}>
            {assessment?.referenceDate
              ? formatAdminDate(assessment.referenceDate, language)
              : unavailable}
          </Field>
        </Subsection>

        <Subsection title={t('admin.prospection.importedProperty.section.registry')}>
          <Field label={t('admin.prospection.importedProperty.registry.activityType')}>
            {activity?.activityType
              ? adminRegistryActivityTypeLabel(activity.activityType, t) ||
                activity.originalLabel ||
                unavailable
              : unavailable}
          </Field>
          <Field label={t('admin.prospection.importedProperty.registry.originalLabel')}>
            {displayValue(activity?.originalLabel, unavailable)}
          </Field>
          <Field label={t('admin.prospection.importedProperty.registry.activityDate')}>
            {activity?.activityDate ? formatAdminDate(activity.activityDate, language) : unavailable}
          </Field>
          <Field label={t('admin.prospection.importedProperty.registry.consideration')}>
            {centsLabel(activity?.considerationCents ?? null)}
          </Field>
          <Field label={t('admin.prospection.importedProperty.registry.registrationNumber')}>
            {displayValue(activity?.registrationNumber, unavailable)}
          </Field>
          <Field label={t('admin.prospection.importedProperty.registry.transferredPercentage')}>
            {activity?.transferredPercentage != null
              ? `${activity.transferredPercentage}%`
              : unavailable}
          </Field>
        </Subsection>

        <Subsection title={t('admin.prospection.importedProperty.section.source')}>
          <Field label={t('admin.prospection.importedProperty.source.label')}>
            {provenance?.sourceType
              ? adminImportProvenanceSourceLabel(provenance.sourceType, t) || unavailable
              : unavailable}
          </Field>
          <Field label={t('admin.prospection.importedProperty.source.importedAt')}>
            {provenance?.importedAt
              ? formatAdminDate(provenance.importedAt, language)
              : ADMIN_DATE_PLACEHOLDER}
          </Field>
        </Subsection>
      </div>
    </Tag>
  );
}
